<template>
  <div class="app-shell">
    <div class="atmos" aria-hidden="true">
      <span class="blob blob--volt"></span>
      <span class="blob blob--sky"></span>
      <span class="blob blob--forest"></span>
    </div>
    <router-view />
  </div>
</template>

<script setup></script>

<style>
:root {
  /* Matches neutralfuels.com dark-editorial pairing (forest #384438 + volt-400) */
  --volt: #c8ff4a;
  --volt-soft: #dcff8a;
  --sky: #74d0ea;

  --text: #eef2ea;
  --muted: rgba(238, 242, 234, 0.62);
  --muted-2: rgba(238, 242, 234, 0.4);

  --line: rgba(255, 255, 255, 0.12);
  --card: rgba(255, 255, 255, 0.05);
  --card-2: rgba(255, 255, 255, 0.07);

  --radius: 26px;
  --radius-sm: 16px;

  --font-display: "Inter", system-ui, -apple-system, sans-serif;
  --font-body: "Inter", system-ui, -apple-system, sans-serif;

  --shadow-soft: 0 1px 2px rgba(0, 0, 0, 0.22), 0 24px 54px -32px rgba(0, 0, 0, 0.72);
  --shadow-lift: 0 2px 6px rgba(0, 0, 0, 0.28), 0 36px 72px -34px rgba(0, 0, 0, 0.82);
}

* {
  box-sizing: border-box;
}

html {
  -webkit-text-size-adjust: 100%;
  overflow-x: hidden;
}

body {
  margin: 0;
  font-family: var(--font-body);
  color: var(--text);
  background: #384438;
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
  overflow-x: hidden;
}

.app-shell {
  position: relative;
  min-height: 100vh;
  width: 100%;
  overflow: hidden;
  isolation: isolate;
  background:
    radial-gradient(125% 80% at 85% -8%, rgba(116, 208, 234, 0.1) 0%, transparent 52%),
    radial-gradient(120% 95% at -8% 108%, rgba(200, 255, 74, 0.12) 0%, transparent 50%),
    #384438;
}

/* Drifting forest + volt atmosphere (matches the site's CinematicHero orbs) */
.atmos {
  position: fixed;
  inset: 0;
  z-index: -1;
  pointer-events: none;
  overflow: hidden;
}

.blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(64px);
  opacity: 0.24;
  will-change: transform;
}

.blob--volt {
  width: 46vw;
  height: 46vw;
  left: -10vw;
  bottom: -14vw;
  background: radial-gradient(circle at 32% 32%, var(--volt), transparent 66%);
  animation: drift-a 22s ease-in-out infinite;
}

.blob--sky {
  width: 40vw;
  height: 40vw;
  right: -12vw;
  top: -16vw;
  background: radial-gradient(circle at 60% 40%, var(--sky), transparent 68%);
  opacity: 0.2;
  animation: drift-b 26s ease-in-out infinite;
}

.blob--forest {
  width: 30vw;
  height: 30vw;
  right: 16vw;
  bottom: -10vw;
  background: radial-gradient(circle, #2f7d46, transparent 70%);
  opacity: 0.32;
  animation: drift-a 30s ease-in-out infinite reverse;
}

@keyframes drift-a {
  0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
  50% { transform: translate3d(4vw, -3vw, 0) scale(1.08); }
}

@keyframes drift-b {
  0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
  50% { transform: translate3d(-3vw, 4vw, 0) scale(1.1); }
}

/* Fine grain for tactile depth */
.app-shell::after {
  content: "";
  position: fixed;
  inset: 0;
  z-index: -1;
  pointer-events: none;
  opacity: 0.06;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
}

@media (prefers-reduced-motion: reduce) {
  .blob { animation: none !important; }
}
</style>
